import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDownLeft,
  ArrowRightLeft,
  ArrowUpRight,
  BookCheck,
  CheckCircle2,
  CircleDashed,
  Download,
  ExternalLink,
  FileText,
  Link2,
  Link2Off,
  ReceiptText,
  RefreshCcw,
  RotateCcw,
  Search,
  Send,
  Sparkles,
  Trash2,
  Upload,
  Wand2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { formatCurrency, formatDate, formatDateTime, formatRelative } from '@/lib/formatters';
import {
  useAiCategorySuggestion,
  useChartOfAccounts,
  useLinkTransactionsAsTransfer,
  usePostTransactionsToQbo,
  useQbEntities,
  useSuggestedQbCategoriesBatch,
  useTransaction,
  useTransactionSplits,
  useTransferLinkCandidates,
  useUnlinkTransactionFromTransfer,
  useUpdateTransactionEntity,
  useUpdateTransactionQbAccount,
  useUpdateTransactionTextFields,
  type QbEntityPick,
  type TransactionDetail,
  type TransferLinkCandidate,
} from '@/features/financials/hooks';
import {
  buildCoaHierarchyByType,
  findLockedAccountIds,
  formatQbAccountLabel,
  QB_GROUP_ORDER,
} from '@/features/financials/coaHierarchy';
import { TransactionSplitsCard } from '@/features/financials/TransactionSplitsCard';
import {
  useReceiptSignedUrl,
  useRemoveReceipt,
  useUpdateExpenseExplanation,
  useUploadReceipt,
} from '@/features/expenses/hooks';
import {
  bankFailLabel,
  isBankFailStatus,
} from '@/features/financials/bankStatus';
import {
  classifyQboPostFailure,
  resolveFailureActionHref,
} from '@/features/financials/qboPostFailure';
import { ExpenseStatusEditor } from '@/features/expenses/ExpenseStatus';
import { TransactionSharingCard } from '@/features/expenses/TransactionSharingCard';
import type { QbPostStatus, Tables } from '@/types/database';

// Sentinel for the QB Category dropdown; Radix Select can't carry an empty
// value so we round-trip through this constant and translate to null at the
// DB boundary (same convention as the list view and ExpenseDetailPage).
const QB_NONE = '__none__';

// Prefix for SelectItems rendered inside the "Suggested" group. See
// TransactionsPage for the rationale — Radix requires unique values per
// Select, but a suggested account is by definition also rendered in its
// real group below. Stripped in onChangeCategory before persistence.
const QB_SUGGEST_PREFIX = 'suggest:';

// Prefixes for the Name (Vendor / Customer) picker. Vendors and customers
// share an `id` namespace per-table but collide across, so we tag the
// type into the SelectItem value and parse it back on change.
const ENTITY_VENDOR_PREFIX = 'vendor:';
const ENTITY_CUSTOMER_PREFIX = 'customer:';

function encodeEntityValue(
  entity: Pick<QbEntityPick, 'type' | 'id'> | null,
): string {
  if (!entity) return QB_NONE;
  return entity.type === 'vendor'
    ? `${ENTITY_VENDOR_PREFIX}${entity.id}`
    : `${ENTITY_CUSTOMER_PREFIX}${entity.id}`;
}

function decodeEntityValue(
  value: string,
): { type: 'vendor' | 'customer'; id: string } | null {
  if (value === QB_NONE) return null;
  if (value.startsWith(ENTITY_VENDOR_PREFIX)) {
    return { type: 'vendor', id: value.slice(ENTITY_VENDOR_PREFIX.length) };
  }
  if (value.startsWith(ENTITY_CUSTOMER_PREFIX)) {
    return { type: 'customer', id: value.slice(ENTITY_CUSTOMER_PREFIX.length) };
  }
  return null;
}

/**
 * Super_admin-facing detail page for a single transaction. Covers every
 * Mercury / Plaid row — card swipes, wires, ACH, deposits — and is the
 * "deep dive" complement to the list view on TransactionsPage.
 *
 * For card-swipe debits the expense workflow (explanation + receipt) is
 * editable inline so super_admin doesn't have to bounce out to the
 * Expense Tracking detail page just to fix a missing field. Both pages
 * point at the same `transactions` row, so an edit here is reflected on
 * the Expense Detail page (and vice versa) on next load. The Expense
 * Tracking detail page still lives at /financials/expenses/<id> as the
 * employee-facing surface; we link to it as a secondary action.
 */
export function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: tx, isLoading, error } = useTransaction(id);
  const { data: coa } = useChartOfAccounts();
  const { data: qbEntities } = useQbEntities();
  // Splits are read here (not just inside TransactionSplitsCard) because
  // canPost needs to know whether the row is categorised via the
  // single-category picker OR via splits — without this read, a
  // splits-only row (with `tx.qb_account_id === null`) would fail
  // canPost and the "Post to QuickBooks" button would never appear.
  const { data: splits } = useTransactionSplits(id);
  const updateQbAccount = useUpdateTransactionQbAccount();
  const updateEntity = useUpdateTransactionEntity();
  const updateTextFields = useUpdateTransactionTextFields();
  const postTransactions = usePostTransactionsToQbo();
  // Transfer-pair mutations. Auto-pairing already happens at sync time
  // (mercury-sync / plaid-sync call match_transaction_transfers); these
  // two are the manual override path for the cases the heuristic missed.
  const linkTransfer = useLinkTransactionsAsTransfer();
  const unlinkTransfer = useUnlinkTransactionFromTransfer();
  // Expense workflow mutations — only invoked from the inline expense
  // form below (card-swipe rows). Same hooks ExpenseDetailPage uses, so
  // both surfaces share a single source of truth on the server.
  const updateExplanation = useUpdateExpenseExplanation();
  const uploadReceipt = useUploadReceipt();
  const removeReceipt = useRemoveReceipt();

  // Modal-open state for the manual pairing dialog. Lives at this level
  // (rather than inside the dialog component) so the search query loads
  // lazily — we only fire the candidates query when the dialog is open.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  // Local drafts for the editable description / memo fields. We keep the
  // bank-feed values (tx.description, tx.memo) in their server-owned
  // columns and persist the operator's edits into `*_override`. Effective
  // values used for display (and for the QBO post) are `override ?? feed`.
  //
  // The drafts are synced from the server row only when the row id or its
  // override value actually changes, so a save-and-refetch doesn't blow
  // away an in-progress edit. The ref carries the last-seen server value
  // so we can detect "true" server changes vs. our own optimistic write.
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [memoDraft, setMemoDraft] = useState('');
  const [explanationDraft, setExplanationDraft] = useState('');
  const lastServerOverrides = useRef<{
    id: string | null;
    description: string | null;
    memo: string | null;
    explanation: string | null;
  }>({ id: null, description: null, memo: null, explanation: null });
  useEffect(() => {
    if (!tx) return;
    const prev = lastServerOverrides.current;
    const idChanged = prev.id !== tx.id;
    const descChanged = prev.description !== tx.description_override;
    const memoChanged = prev.memo !== tx.memo_override;
    const explChanged = prev.explanation !== tx.expense_explanation;
    if (idChanged || descChanged) {
      setDescriptionDraft(tx.description_override ?? '');
    }
    if (idChanged || memoChanged) {
      setMemoDraft(tx.memo_override ?? '');
    }
    if (idChanged || explChanged) {
      setExplanationDraft(tx.expense_explanation ?? '');
    }
    lastServerOverrides.current = {
      id: tx.id,
      description: tx.description_override,
      memo: tx.memo_override,
      explanation: tx.expense_explanation,
    };
  }, [tx]);

  // Hidden file input for the receipt picker, mirrored from the Expense
  // Detail page. Using a ref + button avoids the default <input type=file>
  // UI which doesn't match the rest of the form.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Single-element batch — same RPC the list view uses, just keyed off
  // this one transaction so we can highlight historically-likely
  // categories at the top of the picker.
  const suggestionIds = useMemo(() => (id ? [id] : []), [id]);
  const { data: suggestionsByTx } = useSuggestedQbCategoriesBatch(suggestionIds);

  // Claude-powered "what category should this be?" panel that renders
  // under the QB Category dropdown. Wired here at the top of the
  // component so the request is in flight by the time the user has
  // scrolled to the picker, hiding the latency.
  const {
    data: aiSuggestion,
    isLoading: aiSuggestionLoading,
    isFetching: aiSuggestionFetching,
    error: aiSuggestionError,
    refetch: refetchAiSuggestion,
  } = useAiCategorySuggestion(id);

  const coaById = useMemo(() => {
    const m = new Map<string, Tables<'chart_of_accounts'>>();
    for (const a of coa ?? []) m.set(a.id, a);
    return m;
  }, [coa]);

  const qbAccountsByType = useMemo(() => buildCoaHierarchyByType(coa), [coa]);
  const lockedAccountIds = useMemo(() => findLockedAccountIds(coa), [coa]);

  // Card-swipe transactions can already carry an uploaded receipt via the
  // employee Expense Tracking flow. We mint a signed URL here too so the
  // super_admin can preview it in-line without bouncing to the other page.
  const { data: receiptUrl } = useReceiptSignedUrl(tx?.id, tx?.receipt_path ?? null);

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader
          title="Transaction"
        />
      </div>
    );
  }

  if (isLoading || !tx) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader title="Loading transaction…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCardSwipe = !!tx.card_id;
  const sourceLabel = tx.cards
    ? `${tx.cards.name_on_card ?? 'Card'} •${tx.cards.last_four ?? '????'}`
    : (tx.bank_accounts?.name ?? '—');
  const directionLabel = tx.direction === 'credit' ? 'Money in' : 'Money out';
  // Bank-side failure (Mercury: failed | cancelled | reversed | blocked)
  // takes precedence over the QBO lifecycle in every UI decision below:
  // no posting, a top-of-page warning banner, and the bank status surfaced
  // in the Overview KVs. The DB trigger has already pinned
  // qb_post_status='skipped' (migration 70) so this is purely UX.
  const bankFailedLabel = bankFailLabel(tx.bank_status);
  const bankFailed = isBankFailStatus(tx.bank_status);
  // QBO posting is allowed for any row that's categorised, except while the
  // edge function is mid-flight ('posting'). For 'posted' rows the same
  // button drives an in-place overwrite of the booked QBO Purchase — the
  // edge function detects `qb_external_id` and switches to a PUT-style
  // update (sparse:false) instead of a create. This is how the operator
  // pushes a corrected description / memo / category / payee after the
  // fact without creating a duplicate.
  const hasSplits = (splits ?? []).length > 0;
  const canPost =
    (!!tx.qb_account_id || hasSplits) &&
    !bankFailed &&
    (tx.qb_post_status === 'pending' ||
      tx.qb_post_status === 'failed' ||
      tx.qb_post_status === 'posted');

  const onChangeCategory = async (next: string) => {
    const stripped = next.startsWith(QB_SUGGEST_PREFIX)
      ? next.slice(QB_SUGGEST_PREFIX.length)
      : next;
    const qb_account_id = stripped === QB_NONE ? null : stripped;
    try {
      await updateQbAccount.mutateAsync({ id: tx.id, qb_account_id });
      toast.success(qb_account_id ? 'Category updated' : 'Category cleared');
    } catch (e) {
      toast.error(messageOf(e, 'Update failed'));
    }
  };

  const onChangeEntity = async (next: string) => {
    const entity = decodeEntityValue(next);
    try {
      await updateEntity.mutateAsync({ id: tx.id, entity });
      toast.success(entity ? 'Name updated' : 'Name cleared');
    } catch (e) {
      toast.error(messageOf(e, 'Update failed'));
    }
  };

  // Was this click an initial post or an overwrite of an already-posted
  // row? We capture it before the optimistic flip so the toast and the
  // error string read sensibly to the operator. `isUpdate` only changes
  // the user-facing wording — the edge function picks create vs update
  // off `qb_external_id` server-side.
  const isUpdate = tx.qb_post_status === 'posted';
  const onPost = async () => {
    try {
      const res = await postTransactions.mutateAsync({ transaction_ids: [tx.id] });
      const result = res.results.find((r) => r.id === tx.id);
      if (result?.status === 'failed') {
        toast.error(`${isUpdate ? 'Update' : 'Post'} failed: ${result.error ?? 'unknown error'}`);
      } else if (result?.recreated) {
        // The original QBO entity was missing (deleted/voided in QBO
        // since our last post) so the edge function created a fresh
        // one. Tell the operator so they know the QBO record id has
        // changed under them.
        toast.success(
          'The original entry was gone in QuickBooks — created a fresh one in its place.',
          { duration: 6000 },
        );
      } else {
        toast.success(isUpdate ? 'Updated in QuickBooks.' : 'Posted to QuickBooks.');
      }
      // Receipt attachment is mirrored to QBO in the same call but as a
      // separate, non-fatal step. Surface its outcome in a second toast
      // so the operator can see whether the receipt followed the row.
      // Silent for 'none' / 'unchanged' — nothing meaningful happened.
      if (result?.attachment === 'failed') {
        toast.warning(
          `Posted, but couldn't attach the receipt in QuickBooks: ${
            result.attachment_error ?? 'unknown error'
          }. The post will retry the upload next time.`,
          { duration: 8000 },
        );
      } else if (result?.attachment === 'uploaded') {
        toast.success('Receipt attached in QuickBooks.');
      } else if (result?.attachment === 'replaced') {
        toast.success('Receipt updated in QuickBooks.');
      } else if (result?.attachment === 'removed') {
        toast.success('Receipt removed from QuickBooks.');
      }
    } catch (e) {
      toast.error(messageOf(e, `${isUpdate ? 'Update' : 'Post'} failed`));
    }
  };

  // Edits to description / memo stay editable after the row is posted:
  // saving an override here doesn't touch QBO directly, it just updates
  // Atlas's source of truth. Pushing the override into QBO happens when
  // the operator clicks Update QuickBooks below (the edge function then
  // PUTs the updated payload on the existing Purchase). 'posting' is
  // the only state we still lock, since the edge function is mid-flight
  // and a write would race the response.
  const textFieldsDisabled =
    updateTextFields.isPending || tx.qb_post_status === 'posting';

  // "Effective" values are what we display in the page header, the QBO
  // post, and the editable inputs as their initial value. Override beats
  // feed; the trim mirrors the Edge Function's logic so an all-whitespace
  // override doesn't show as the effective value here either.
  const effectiveDescription =
    tx.description_override && tx.description_override.trim()
      ? tx.description_override
      : tx.description;

  // Save handlers: persist override columns, normalising empty strings to
  // NULL so "clear the field" reverts to the bank-feed value (instead of
  // pinning an empty string override on top of it).
  const onSaveDescription = async () => {
    const trimmed = descriptionDraft.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    if (next === (tx.description_override ?? null)) return;
    try {
      await updateTextFields.mutateAsync({ id: tx.id, description_override: next });
      toast.success(next ? 'Description updated' : 'Description reset to feed value');
    } catch (e) {
      toast.error(messageOf(e, 'Update failed'));
    }
  };

  const onSaveMemo = async () => {
    const trimmed = memoDraft.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    if (next === (tx.memo_override ?? null)) return;
    try {
      await updateTextFields.mutateAsync({ id: tx.id, memo_override: next });
      toast.success(next ? 'Memo updated' : 'Memo reset to default');
    } catch (e) {
      toast.error(messageOf(e, 'Update failed'));
    }
  };

  const onResetDescription = async () => {
    setDescriptionDraft('');
    if (tx.description_override === null) return;
    try {
      await updateTextFields.mutateAsync({ id: tx.id, description_override: null });
      toast.success('Description reset to feed value');
    } catch (e) {
      toast.error(messageOf(e, 'Reset failed'));
    }
  };

  const onResetMemo = async () => {
    setMemoDraft('');
    if (tx.memo_override === null) return;
    try {
      await updateTextFields.mutateAsync({ id: tx.id, memo_override: null });
      toast.success('Memo reset to default');
    } catch (e) {
      toast.error(messageOf(e, 'Reset failed'));
    }
  };

  const categoryDisabled =
    updateQbAccount.isPending ||
    (coa ?? []).length === 0 ||
    tx.qb_post_status === 'posting';

  // Expense-workflow fields (explanation + receipt) follow ExpenseDetailPage:
  // locked the moment the row is live in QBO so Atlas and QuickBooks can't
  // silently desync, and locked while a post is mid-flight to avoid racing
  // the Edge Function's response.
  const expenseFieldsDisabled =
    tx.qb_post_status === 'posted' || tx.qb_post_status === 'posting';

  const onSaveExplanation = async () => {
    const trimmed = explanationDraft.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    if (next === (tx.expense_explanation ?? null)) return;
    try {
      await updateExplanation.mutateAsync({
        id: tx.id,
        expense_explanation: next,
      });
      toast.success(next ? 'Explanation saved' : 'Explanation cleared');
    } catch (e) {
      toast.error(messageOf(e, 'Save failed'));
    }
  };

  const onPickReceiptFile = () => fileInputRef.current?.click();

  const onReceiptFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same filename
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Receipt must be under 50 MB.');
      return;
    }
    try {
      await uploadReceipt.mutateAsync({
        transactionId: tx.id,
        file,
        previousPath: tx.receipt_path,
      });
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error(messageOf(err, 'Upload failed'));
    }
  };

  const onRemoveReceipt = async () => {
    if (!tx.receipt_path) return;
    try {
      await removeReceipt.mutateAsync({
        transactionId: tx.id,
        path: tx.receipt_path,
      });
      toast.success('Receipt removed');
    } catch (e) {
      toast.error(messageOf(e, 'Could not remove receipt'));
    }
  };

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        eyebrow="Financials / Transactions"
        title={effectiveDescription}
        meta={
          <>
            <span className="tabular-nums">{formatCurrency(tx.amount_cents)}</span>
            {' · '}
            {formatDateTime(tx.posted_at)}
            {' · '}
            {sourceLabel}
          </>
        }
        actions={
          bankFailedLabel ? (
            <Badge variant="warning" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Bank: {bankFailedLabel}
            </Badge>
          ) : (
            <QbPostBadge status={tx.qb_post_status} />
          )
        }
      />

      {/* Top-of-page banners that quickly explain unusual states. The
          bank-failure banner is rendered first because it overrides
          everything else — there's nothing to post to QBO and no QBO
          error to act on if the bank itself rejected the transaction. */}
      {bankFailedLabel && (
        <Banner
          tone="warning"
          title={`Mercury reported this transaction as ${bankFailedLabel.toLowerCase()}`}
        >
          The money never moved at the bank — nothing to post to QuickBooks.
        </Banner>
      )}
      {!bankFailedLabel && tx.qb_post_status === 'failed' && (
        // Walk the raw qb_last_error through the classifier so the
        // banner shows the operator's *next action* (Reconnect QBO, Map
        // bank account, Pick a different category, etc.) instead of the
        // bare error string. The raw error stays accessible — both in
        // the <details> below and in the Overview KV card at the
        // bottom — so an engineer debugging an unfamiliar failure
        // doesn't lose the literal message.
        (() => {
          const failure = classifyQboPostFailure(tx.qb_last_error);
          const href = resolveFailureActionHref(failure.action, tx.id);
          const renderAction = () => {
            if (failure.action.kind === 'none') return null;
            if (failure.action.kind === 'retry') {
              return (
                <Button
                  size="sm"
                  variant="default"
                  onClick={onPost}
                  disabled={!canPost || postTransactions.isPending}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {failure.action.longLabel}
                </Button>
              );
            }
            if (!href) return null;
            return (
              <Button asChild size="sm" variant="default" className="gap-1.5">
                <Link to={href}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  {failure.action.longLabel}
                </Link>
              </Button>
            );
          };
          return (
            <div className="rounded-lg border border-fresh-salmon-dark/30 bg-fresh-salmon-dark/10 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    {failure.headline}
                  </p>
                  <p className="text-muted-foreground">{failure.description}</p>
                </div>
                {renderAction()}
              </div>
              {tx.qb_last_error && (
                <details className="mt-2 text-xs text-muted-foreground/80">
                  <summary className="cursor-pointer select-none">
                    Raw QuickBooks error
                  </summary>
                  <pre className="mt-1 whitespace-pre-wrap break-words font-mono">
                    {tx.qb_last_error}
                  </pre>
                </details>
              )}
            </div>
          );
        })()
      )}
      {tx.qb_post_status === 'posted' && tx.qb_external_id && (
        <Banner tone="success" title="Posted to QuickBooks">
          QBO record id <span className="font-mono">{tx.qb_external_id}</span>
          {tx.qb_posted_at && (
            <span className="block pt-1 text-xs text-muted-foreground">
              {formatRelative(tx.qb_posted_at)}
            </span>
          )}
        </Banner>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <KV
                label="Direction"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    {tx.direction === 'credit' ? (
                      <ArrowDownLeft className="h-3.5 w-3.5 text-bright-moss-dark" />
                    ) : (
                      <ArrowUpRight className="h-3.5 w-3.5 text-fresh-salmon-dark" />
                    )}
                    {directionLabel}
                  </span>
                }
              />
              <KV
                label="Amount"
                value={
                  <span
                    className={
                      'tabular-nums ' +
                      (tx.direction === 'credit'
                        ? 'text-bright-moss-dark'
                        : 'text-foreground')
                    }
                  >
                    {tx.direction === 'credit' ? '+' : '−'}
                    {formatCurrency(tx.amount_cents)}
                  </span>
                }
              />
              <KV label="Posted" value={formatDateTime(tx.posted_at)} />
              <KV label="Synced" value={formatRelative(tx.created_at)} />
              {tx.bank_status && (
                <KV
                  label="Bank status"
                  value={
                    bankFailedLabel ? (
                      <span className="inline-flex items-center gap-1.5 text-fresh-salmon-dark">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {bankFailedLabel}
                      </span>
                    ) : (
                      <span className="capitalize">{tx.bank_status}</span>
                    )
                  }
                />
              )}
              <KV
                label="Name"
                value={
                  tx.qb_vendor?.name ??
                  tx.qb_customer?.name ??
                  // No mapping yet — show the bank-feed counterparty as a
                  // muted hint so super_admin sees the auto-fill source.
                  (tx.counterparty ? (
                    <span className="text-muted-foreground">
                      {tx.counterparty}{' '}
                      <span className="text-[11px] uppercase tracking-wide">
                        (from feed)
                      </span>
                    </span>
                  ) : (
                    '—'
                  ))
                }
              />
              <KV
                label="Source"
                value={
                  tx.cards ? (
                    <span>
                      {tx.cards.name_on_card ?? 'Card'} •{tx.cards.last_four ?? '????'}
                      {tx.bank_accounts?.name && (
                        <span className="block text-xs text-muted-foreground">
                          on {tx.bank_accounts.name}
                        </span>
                      )}
                    </span>
                  ) : (
                    (tx.bank_accounts?.name ?? '—')
                  )
                }
              />
              {tx.category && <KV label="Provider category" value={tx.category} />}
              {tx.external_id && (
                <KV
                  label="External ID"
                  value={<span className="font-mono text-xs">{tx.external_id}</span>}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Description &amp; memo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="tx-description">Description</Label>
                  {tx.description_override !== null && !textFieldsDisabled && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={onResetDescription}
                      disabled={updateTextFields.isPending}
                      className="h-auto px-2 py-1 text-xs"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset to feed
                    </Button>
                  )}
                </div>
                <Input
                  id="tx-description"
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  onBlur={onSaveDescription}
                  placeholder={tx.description}
                  disabled={textFieldsDisabled}
                />
                <p className="text-xs text-muted-foreground">
                  From feed:{' '}
                  <span className="font-medium text-foreground">{tx.description}</span>
                  {tx.description_override !== null && (
                    <span className="ml-1 text-[11px] uppercase tracking-wide text-atmosphere-dark">
                      · edited
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="tx-memo">Memo (QuickBooks)</Label>
                  {tx.memo_override !== null && !textFieldsDisabled && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={onResetMemo}
                      disabled={updateTextFields.isPending}
                      className="h-auto px-2 py-1 text-xs"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset
                    </Button>
                  )}
                </div>
                <Textarea
                  id="tx-memo"
                  rows={3}
                  value={memoDraft}
                  onChange={(e) => setMemoDraft(e.target.value)}
                  onBlur={onSaveMemo}
                  placeholder={
                    tx.memo ??
                    `Defaults to the description: "${effectiveDescription}"`
                  }
                  disabled={textFieldsDisabled}
                />
                {tx.memo && (
                  <p className="text-xs text-muted-foreground">
                    From feed:{' '}
                    <span className="font-medium text-foreground">{tx.memo}</span>
                    {tx.memo_override !== null && (
                      <span className="ml-1 text-[11px] uppercase tracking-wide text-atmosphere-dark">
                        · edited
                      </span>
                    )}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Name (QuickBooks payee)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const vendors = qbEntities?.vendors ?? [];
                const customers = qbEntities?.customers ?? [];
                const hasEntities = vendors.length > 0 || customers.length > 0;
                const currentValue = tx.qb_vendor_id
                  ? encodeEntityValue({ type: 'vendor', id: tx.qb_vendor_id })
                  : tx.qb_customer_id
                    ? encodeEntityValue({ type: 'customer', id: tx.qb_customer_id })
                    : QB_NONE;
                const entityDisabled =
                  updateEntity.isPending ||
                  !hasEntities ||
                  tx.qb_post_status === 'posting';
                return (
                  <>
                    <Select
                      value={currentValue}
                      onValueChange={onChangeEntity}
                      disabled={entityDisabled}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            !hasEntities
                              ? 'Sync QuickBooks first…'
                              : (tx.counterparty ?? 'Pick a name')
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={QB_NONE}>
                          <span className="text-muted-foreground">Unassigned</span>
                        </SelectItem>
                        {vendors.length > 0 && (
                          <SelectGroup>
                            <SelectSeparator />
                            <SelectLabel>Vendors</SelectLabel>
                            {vendors.map((v) => (
                              <SelectItem
                                key={`v-${v.id}`}
                                value={encodeEntityValue(v)}
                              >
                                {v.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        {customers.length > 0 && (
                          <SelectGroup>
                            <SelectSeparator />
                            <SelectLabel>Customers</SelectLabel>
                            {customers.map((c) => (
                              <SelectItem
                                key={`c-${c.id}`}
                                value={encodeEntityValue(c)}
                              >
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                    {tx.counterparty && (
                      <p className="text-xs text-muted-foreground">
                        Bank feed reported{' '}
                        <span className="font-medium text-foreground">
                          {tx.counterparty}
                        </span>
                        .
                      </p>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>QuickBooks category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select
                value={tx.qb_account_id ?? QB_NONE}
                onValueChange={onChangeCategory}
                disabled={categoryDisabled}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      (coa ?? []).length === 0 ? 'Sync QuickBooks first…' : 'Pick a category'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={QB_NONE}>
                    <span className="text-muted-foreground">Unassigned</span>
                  </SelectItem>
                  {(() => {
                    // "Suggested" group: up to three previously-used
                    // categories for transactions with this vendor key.
                    // Skip suggesting whatever is already assigned —
                    // pointing at the current value adds no information.
                    // Also skip locked parent accounts; we can't post to
                    // those even though a historical row may reference
                    // one if a leaf was demoted to a sub-account after
                    // the post.
                    const ids = suggestionsByTx?.get(tx.id) ?? [];
                    const items = ids
                      .filter((sid) => sid !== tx.qb_account_id && !lockedAccountIds.has(sid))
                      .map((sid) => coaById.get(sid))
                      .filter((a): a is Tables<'chart_of_accounts'> => !!a && a.is_active);
                    if (items.length === 0) return null;
                    return (
                      <SelectGroup>
                        <SelectSeparator />
                        <SelectLabel className="flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 text-atmosphere-dark" />
                          Suggested
                        </SelectLabel>
                        {items.map((a) => (
                          <SelectItem
                            key={`suggest-${a.id}`}
                            value={`${QB_SUGGEST_PREFIX}${a.id}`}
                          >
                            {formatQbAccountLabel(a)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })()}
                  {QB_GROUP_ORDER.map(({ key, label }) => {
                    const items = qbAccountsByType.get(key) ?? [];
                    if (items.length === 0) return null;
                    return (
                      <SelectGroup key={key}>
                        <SelectSeparator />
                        <SelectLabel>{label}</SelectLabel>
                        {items.map(({ account, depth, locked }) => (
                          <SelectItem
                            key={account.id}
                            value={account.id}
                            disabled={locked}
                            className={
                              locked
                                ? 'pl-8 text-muted-foreground/60 data-[disabled]:opacity-100'
                                : 'pl-8'
                            }
                            style={{ paddingLeft: `${2 + depth * 1.25}rem` }}
                          >
                            {formatQbAccountLabel(account)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>

              {/* AI category suggestion — Claude reads the description /
                memo / explanation / vendor / amount + the org's full QB
                category list + every historic Atlas and pre-cutover QBO
                categorisation for this same vendor, then proposes the
                single best category and a 1–2 sentence rationale. The
                "Apply" affordance only renders when the model picked
                something different from what's already on the row. */}
              <AiCategorySuggestionPanel
                loading={aiSuggestionLoading || aiSuggestionFetching}
                error={aiSuggestionError instanceof Error ? aiSuggestionError.message : null}
                suggestion={aiSuggestion?.suggestion ?? null}
                currentQbAccountId={tx.qb_account_id}
                disabled={categoryDisabled}
                onApply={(qbId) => onChangeCategory(qbId)}
                onRetry={() => {
                  void refetchAiSuggestion();
                }}
              />
            </CardContent>
          </Card>

          {/* Manual category splits — operator can break this transaction
              into multiple QB categories (e.g. family-loan principal +
              interest, or an Amazon charge spanning Office Supplies +
              Software + Shop Tools). When at least one split is saved,
              Atlas posts a multi-line Purchase / Deposit and ignores the
              single QB Category picker above. The card itself renders
              its toggle so it always sits in the layout — no jarring
              "now you see it, now you don't" flicker. Locked when a row
              is mid-post / bank-failed / in_qb. */}
          <TransactionSplitsCard
            transactionId={tx.id}
            amountCents={tx.amount_cents}
            disabled={
              tx.qb_post_status === 'posting' ||
              isBankFailStatus(tx.bank_status) ||
              tx.expense_status === 'in_qb'
            }
            disabledReason={
              tx.qb_post_status === 'posting'
                ? 'Locked while this row is mid-post to QuickBooks.'
                : isBankFailStatus(tx.bank_status)
                  ? 'Disabled — the bank reported this transaction as never-settled.'
                  : tx.expense_status === 'in_qb'
                    ? 'Disabled — this row is marked "Already in QuickBooks".'
                    : null
            }
          />

          {isCardSwipe && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <ReceiptText className="h-4 w-4 text-muted-foreground" />
                      Expense workflow
                    </CardTitle>
                  </div>
                  <ExpenseStatusEditor
                    transactionId={tx.id}
                    status={tx.expense_status}
                    qbPostStatus={tx.qb_post_status}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="tx-explanation">Explanation</Label>
                  <Textarea
                    id="tx-explanation"
                    rows={4}
                    value={explanationDraft}
                    onChange={(e) => setExplanationDraft(e.target.value)}
                    onBlur={onSaveExplanation}
                    placeholder="e.g. Annual subscription renewal for our project-management tool."
                    disabled={expenseFieldsDisabled || updateExplanation.isPending}
                  />
                </div>

                <div className="space-y-3">
                  <Label>Receipt</Label>
                  {tx.receipt_path ? (
                    <ReceiptPreview
                      url={receiptUrl ?? null}
                      path={tx.receipt_path}
                      uploadedAt={tx.receipt_uploaded_at}
                    />
                  ) : (
                    <div className="rounded-md border border-dashed border-border bg-secondary/30 p-4 text-center text-sm text-muted-foreground">
                      No receipt attached yet.
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={onReceiptFileSelected}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onPickReceiptFile}
                      disabled={expenseFieldsDisabled || uploadReceipt.isPending}
                    >
                      <Upload className="h-4 w-4" />
                      {uploadReceipt.isPending
                        ? 'Uploading…'
                        : tx.receipt_path
                          ? 'Replace receipt'
                          : 'Upload receipt'}
                    </Button>
                    {tx.receipt_path && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onRemoveReceipt}
                        disabled={
                          expenseFieldsDisabled || removeReceipt.isPending
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>

                {tx.rejection_reason && tx.expense_status === 'rejected' && (
                  <p className="text-xs text-fresh-salmon-dark">
                    Rejected: {tx.rejection_reason}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Link to={`/financials/expenses/${tx.id}`}>
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open employee view
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-transaction sharing — the cardholder sees this row by
              default; add anyone else (e.g. the person who actually made a
              purchase on a general company card) so they can attach the
              receipt + explanation from their own My expenses page. */}
          {isCardSwipe && (
            <TransactionSharingCard transactionId={tx.id} cardId={tx.card_id} />
          )}

          {/* Non-card transactions (wires, ACH, deposits, bank transfers)
              don't go through the card-swipe expense workflow, but
              super_admin still often wants a receipt / invoice on file for
              QuickBooks documentation. The storage RLS + QBO Attachable sync
              already support any transaction, so we surface a standalone
              receipt uploader here. Same posted/posting lock as the
              card-swipe path so Atlas and QBO can't silently desync. */}
          {!isCardSwipe && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ReceiptText className="h-4 w-4 text-muted-foreground" />
                  Receipt / documentation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tx.receipt_path ? (
                  <ReceiptPreview
                    url={receiptUrl ?? null}
                    path={tx.receipt_path}
                    uploadedAt={tx.receipt_uploaded_at}
                  />
                ) : (
                  <div className="rounded-md border border-dashed border-border bg-secondary/30 p-4 text-center text-sm text-muted-foreground">
                    No receipt attached yet.
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*,application/pdf"
                    onChange={onReceiptFileSelected}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onPickReceiptFile}
                    disabled={expenseFieldsDisabled || uploadReceipt.isPending}
                  >
                    <Upload className="h-4 w-4" />
                    {uploadReceipt.isPending
                      ? 'Uploading…'
                      : tx.receipt_path
                        ? 'Replace receipt'
                        : 'Upload receipt'}
                  </Button>
                  {tx.receipt_path && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onRemoveReceipt}
                      disabled={expenseFieldsDisabled || removeReceipt.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <TransferPairCard
            tx={tx}
            onUnlink={async () => {
              try {
                await unlinkTransfer.mutateAsync({ transactionId: tx.id });
                toast.success(
                  'Transfer pair removed. Review the category and approval status — they were auto-set by the matcher and may need adjusting.',
                  { duration: 8000 },
                );
              } catch (e) {
                toast.error(messageOf(e, 'Could not unlink'));
              }
            }}
            onOpenLinkDialog={() => setLinkDialogOpen(true)}
            unlinkPending={unlinkTransfer.isPending}
            linkDialogOpen={linkDialogOpen}
            onLinkDialogOpenChange={setLinkDialogOpen}
            onLink={async (followerId) => {
              try {
                await linkTransfer.mutateAsync({
                  leadId: tx.id,
                  followerId,
                });
                toast.success(
                  'Linked as a transfer pair. The other side has been routed to "Already in QuickBooks" so it can\'t double-post.',
                  { duration: 8000 },
                );
                setLinkDialogOpen(false);
              } catch (e) {
                toast.error(messageOf(e, 'Could not link'));
              }
            }}
            linkPending={linkTransfer.isPending}
          />
          <Card>
            <CardHeader>
              <CardTitle>QuickBooks posting</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Status</span>
                {bankFailedLabel ? (
                  <Badge variant="warning" className="gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Bank: {bankFailedLabel}
                  </Badge>
                ) : (
                  <QbPostBadge status={tx.qb_post_status} />
                )}
              </div>
              {canPost && (
                <Button
                  className="w-full"
                  onClick={onPost}
                  disabled={postTransactions.isPending}
                  variant={isUpdate ? 'outline' : 'default'}
                >
                  <Send className="h-4 w-4" />
                  {postTransactions.isPending
                    ? isUpdate
                      ? 'Updating…'
                      : 'Posting…'
                    : tx.qb_post_status === 'failed'
                      ? 'Retry post'
                      : isUpdate
                        ? 'Update QuickBooks'
                        : 'Post to QuickBooks'}
                </Button>
              )}
              {tx.qb_post_status === 'posted' && tx.qb_external_id && (
                <p className="text-xs text-muted-foreground">
                  QBO record{' '}
                  <span className="font-mono text-foreground">{tx.qb_external_id}</span>
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Audit trail</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-muted-foreground">
              <KV label="Synced from provider" value={formatRelative(tx.created_at)} />
              {tx.submitted_at && (
                <KV label="Submitted" value={formatRelative(tx.submitted_at)} />
              )}
              {tx.approved_at && (
                <KV label="Approved" value={formatRelative(tx.approved_at)} />
              )}
              {tx.receipt_uploaded_at && (
                <KV
                  label="Receipt uploaded"
                  value={formatRelative(tx.receipt_uploaded_at)}
                />
              )}
              {tx.qb_posted_at && (
                <KV label="Posted to QBO" value={formatRelative(tx.qb_posted_at)} />
              )}
              {tx.qb_post_status === 'failed' && tx.qb_last_error && (
                <KV label="QBO error" value={tx.qb_last_error} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/financials/transactions"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to transactions
    </Link>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

function Banner({
  tone,
  title,
  children,
}: {
  tone: 'info' | 'warning' | 'success';
  title: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'success'
      ? 'border-bright-moss-dark/30 bg-bright-moss-dark/10'
      : tone === 'warning'
        ? 'border-fresh-salmon-dark/30 bg-fresh-salmon-dark/10'
        : 'border-atmosphere-dark/30 bg-atmosphere-dark/10';
  return (
    <div className={`rounded-lg border ${toneClass} px-4 py-3 text-sm`}>
      <p className="font-medium text-foreground">{title}</p>
      <p className="text-muted-foreground">{children}</p>
    </div>
  );
}

function QbPostBadge({ status }: { status: QbPostStatus }) {
  switch (status) {
    case 'posted':
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Posted
        </Badge>
      );
    case 'posting':
      return (
        <Badge variant="info" className="gap-1">
          <CircleDashed className="h-3 w-3 animate-spin" />
          Posting…
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="warning" className="gap-1">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
    case 'skipped':
      return <Badge variant="muted">Skipped</Badge>;
    case 'awaiting_approval':
      return (
        <Badge variant="info" className="gap-1">
          <CircleDashed className="h-3 w-3" />
          Awaiting approval
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="info" className="gap-1">
          <Send className="h-3 w-3" />
          Ready to post
        </Badge>
      );
    default:
      return <Badge variant="muted">Unassigned</Badge>;
  }
}

function ReceiptPreview({
  url,
  path,
  uploadedAt,
}: {
  url: string | null;
  path: string;
  uploadedAt?: string | null;
}) {
  const isImage = /\.(png|jpe?g|gif|heic|webp)$/i.test(path);
  const isPdf = /\.pdf$/i.test(path);
  return (
    <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-3">
      {url ? (
        isImage ? (
          <img
            src={url}
            alt="Receipt"
            className="mx-auto max-h-64 w-auto rounded-md border border-border"
          />
        ) : isPdf ? (
          <iframe
            src={url}
            title="Receipt PDF"
            className="h-64 w-full rounded-md border border-border bg-white"
          />
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>Attached file (preview unavailable)</span>
          </div>
        )
      ) : (
        <Skeleton className="h-24 w-full" />
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {uploadedAt ? `Uploaded ${formatRelative(uploadedAt)}` : 'Uploaded'}
          {' · '}
          {fileNameOf(path)}
        </span>
        {url && (
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-atmosphere-dark hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </a>
            <a
              href={url}
              download
              className="inline-flex items-center gap-1 text-xs text-atmosphere-dark hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// Strip the leading `<timestamp>-` we prepend at upload time so the user
// sees their original filename in the preview instead of the storage key.
function fileNameOf(path: string): string {
  const last = path.split('/').pop() ?? path;
  const m = last.match(/^\d+-(.+)$/);
  return m ? m[1] : last;
}

function messageOf(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message;
  return fallback;
}

/**
 * Inline AI-suggested QB category, rendered underneath the picker on the
 * QuickBooks Category card. Three visual states:
 *
 *   - loading  → skeleton + "Atlas is thinking…" caption.
 *   - error    → soft warning bubble + Retry button.
 *   - success  → reasoning paragraph, plus an "Apply" button when the
 *                model picked a category different from the current one.
 *
 * Kept self-contained on purpose: the parent owns the data-fetch (so the
 * request is in-flight before this component mounts) and just passes the
 * resolved state in. Apply / Retry are surfaced as callbacks so this
 * component never touches the mutation hooks directly.
 */
function AiCategorySuggestionPanel({
  loading,
  error,
  suggestion,
  currentQbAccountId,
  disabled,
  onApply,
  onRetry,
}: {
  loading: boolean;
  error: string | null;
  suggestion: {
    qb_account_id: string | null;
    qb_account_name: string | null;
    reasoning: string;
    alternatives_considered: Array<{ label: string; why_not: string }>;
    confidence: 'high' | 'medium' | 'low' | null;
  } | null;
  currentQbAccountId: string | null;
  disabled: boolean;
  onApply: (qbAccountId: string) => void;
  onRetry: () => void;
}) {
  const showApply =
    !!suggestion?.qb_account_id &&
    suggestion.qb_account_id !== currentQbAccountId;

  // Render reasoning as separate paragraphs when Opus breaks it up with
  // blank lines (it sometimes does for the longer responses). Keeps the
  // text readable instead of one wall of prose.
  const reasoningParagraphs =
    suggestion?.reasoning
      ?.split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean) ?? [];

  return (
    <div className="rounded-md border border-atmosphere-dark/30 bg-atmosphere-dark/5 p-3">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Wand2 className="h-3.5 w-3.5 text-atmosphere-dark" />
        <span className="text-xs font-medium uppercase tracking-wide text-atmosphere-dark">
          Atlas AI suggestion
        </span>
        {suggestion?.confidence && (
          <span className="ml-1 rounded-sm bg-atmosphere-dark/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-atmosphere-dark">
            {suggestion.confidence} confidence
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-4/6" />
        </div>
      )}

      {!loading && error && (
        <div className="space-y-2">
          <p className="text-sm text-fresh-salmon-dark">
            Couldn't get a suggestion: {error}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRetry}
            className="h-auto px-2 py-1 text-xs"
          >
            <RefreshCcw className="h-3 w-3" />
            Try again
          </Button>
        </div>
      )}

      {!loading && !error && suggestion && (
        <div className="space-y-3">
          {suggestion.qb_account_name && (
            <p className="text-sm">
              <span className="text-muted-foreground">Suggested category:</span>{' '}
              <span className="font-medium text-foreground">
                {suggestion.qb_account_name}
              </span>
            </p>
          )}
          {reasoningParagraphs.length > 0 && (
            <div className="space-y-2">
              {reasoningParagraphs.map((p, i) => (
                <p key={i} className="text-sm leading-snug text-foreground">
                  {p}
                </p>
              ))}
            </div>
          )}
          {suggestion.alternatives_considered.length > 0 && (
            <div className="space-y-1.5 border-t border-atmosphere-dark/15 pt-2.5">
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Other categories considered
              </p>
              <ul className="space-y-1.5">
                {suggestion.alternatives_considered.map((alt, i) => (
                  <li key={i} className="text-xs leading-snug">
                    <span className="font-medium text-foreground">
                      Why not {alt.label}:
                    </span>{' '}
                    <span className="text-muted-foreground">{alt.why_not}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {showApply && suggestion.qb_account_id && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onApply(suggestion.qb_account_id!)}
                disabled={disabled}
                className="h-auto px-2.5 py-1 text-xs"
              >
                <Sparkles className="h-3 w-3" />
                Apply suggestion
              </Button>
            )}
            {!showApply && suggestion.qb_account_id && (
              <span className="text-xs italic text-muted-foreground">
                Matches the current pick.
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onRetry}
              className="h-auto px-2 py-1 text-xs"
            >
              <RefreshCcw className="h-3 w-3" />
              Re-ask Atlas
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// TransferPairCard — sits in the right rail above QuickBooks posting.
//
// Three states:
//   * `tx.transfer_partner` is null → show a "Not paired" stub with the
//     "Link to another transaction" affordance. Operators rarely need this
//     (the matcher handles 99% of pairs at sync time), but the manual path
//     is the safety valve for: different amounts due to wire fees,
//     off-by-N-day posting lag, and ambiguous matches the matcher
//     deliberately skipped.
//   * Partner is set → show the partner's bank account + amount, a
//     direction arrow that respects who's the lead vs follower, the
//     "Already in QB" / "Skipped" badge on the follower, and an Unlink
//     button. The card also explains in one line what happens at QBO post
//     time so the operator understands why the partner doesn't have its
//     own Post button.
//   * Hidden entirely on bank-failed rows so the page doesn't tease an
//     affordance that wouldn't do anything.
// -----------------------------------------------------------------------------
interface TransferPairCardProps {
  tx: TransactionDetail;
  onUnlink: () => Promise<void> | void;
  onOpenLinkDialog: () => void;
  unlinkPending: boolean;
  linkDialogOpen: boolean;
  onLinkDialogOpenChange: (open: boolean) => void;
  onLink: (followerId: string) => Promise<void> | void;
  linkPending: boolean;
}

function TransferPairCard({
  tx,
  onUnlink,
  onOpenLinkDialog,
  unlinkPending,
  linkDialogOpen,
  onLinkDialogOpenChange,
  onLink,
  linkPending,
}: TransferPairCardProps) {
  // Bank-failed rows can never be a transfer pair (the money never
  // moved), so don't tease the UI. Same gate `qb_post_status='skipped'`
  // uses to hide the Post button.
  if (isBankFailStatus(tx.bank_status)) return null;

  const partner = tx.transfer_partner;

  if (!partner) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
            Transfer pair
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Dialog open={linkDialogOpen} onOpenChange={onLinkDialogOpenChange}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onOpenLinkDialog}
              >
                <Link2 className="h-3.5 w-3.5" />
                Link to another transaction
              </Button>
            </DialogTrigger>
            <TransferLinkDialog
              tx={tx}
              onPick={onLink}
              pending={linkPending}
            />
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  const partnerName =
    partner.bank_accounts?.name ??
    'Other account';
  const partnerDescription =
    partner.description_override?.trim() && partner.description_override.length > 0
      ? partner.description_override
      : partner.description;
  // Lead = direction='debit'; the matcher pinned the auto-categorisation
  // defaults to that side. We render the arrow accordingly so the operator
  // can see at a glance which side hits QBO and which side is suppressed.
  const isLead = tx.direction === 'debit';
  const followerInQb = partner.expense_status === 'in_qb';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-atmosphere-dark" />
          Transfer pair
          {isLead ? (
            <Badge variant="info" className="ml-auto">
              This side posts to QBO
            </Badge>
          ) : (
            <Badge variant="muted" className="ml-auto gap-1">
              <BookCheck className="h-3 w-3" />
              Follower
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Link
          to={`/financials/transactions/${partner.id}`}
          className="block rounded-md border border-border bg-secondary/40 px-3 py-2.5 transition-colors hover:bg-secondary/60"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 space-y-0.5">
              <p className="truncate text-sm font-medium text-foreground">
                {partnerName}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {partnerDescription}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {formatDateTime(partner.posted_at)}
              </p>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span
                className={
                  'tabular-nums text-sm ' +
                  (partner.direction === 'credit'
                    ? 'text-bright-moss-dark'
                    : 'text-foreground')
                }
              >
                {partner.direction === 'credit' ? '+' : '−'}
                {formatCurrency(partner.amount_cents)}
              </span>
              {followerInQb && partner.direction === 'credit' && (
                <Badge variant="muted" className="gap-1 text-[10px]">
                  <BookCheck className="h-2.5 w-2.5" />
                  Already in QB
                </Badge>
              )}
            </div>
          </div>
        </Link>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onUnlink}
            disabled={unlinkPending}
            className="text-xs"
          >
            <Link2Off className="h-3.5 w-3.5" />
            {unlinkPending ? 'Unlinking…' : 'Unlink'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// TransferLinkDialog — picker for the manual "Link to another transaction"
// affordance. We surface candidates with a closeness rank so the operator
// can pick the most-likely partner without scrolling. The list is gated
// behind a Dialog rather than rendered inline so the candidates query
// only fires when the operator opens the picker.
// -----------------------------------------------------------------------------
function TransferLinkDialog({
  tx,
  onPick,
  pending,
}: {
  tx: TransactionDetail;
  onPick: (id: string) => Promise<void> | void;
  pending: boolean;
}) {
  const { data: candidates, isLoading } = useTransferLinkCandidates(tx.id);
  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          Pick the other side of the transfer
        </DialogTitle>
      </DialogHeader>
      <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {!isLoading && (candidates ?? []).length === 0 && (
          <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
            No candidate transactions found.
          </p>
        )}
        {(candidates ?? []).map((c) => (
          <TransferLinkCandidateRow
            key={c.id}
            candidate={c}
            sourceAmountCents={tx.amount_cents}
            onPick={() => onPick(c.id)}
            disabled={pending}
          />
        ))}
      </div>
    </DialogContent>
  );
}

function TransferLinkCandidateRow({
  candidate,
  sourceAmountCents,
  onPick,
  disabled,
}: {
  candidate: TransferLinkCandidate;
  sourceAmountCents: number;
  onPick: () => void;
  disabled: boolean;
}) {
  const exact = candidate.amount_delta_cents === 0;
  const days = candidate.posted_delta_days;
  const dayLabel =
    days === 0 ? 'same day' : `${days > 0 ? '+' : ''}${days}d`;
  const description =
    candidate.description_override?.trim() && candidate.description_override.length > 0
      ? candidate.description_override
      : candidate.description;
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className="block w-full rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-atmosphere-dark/40 hover:bg-secondary/40 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <p className="truncate text-sm font-medium text-foreground">
            {candidate.bank_accounts?.name ?? 'Other account'}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {description}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatDate(candidate.posted_at)} · {dayLabel}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className={
              'tabular-nums text-sm ' +
              (candidate.direction === 'credit'
                ? 'text-bright-moss-dark'
                : 'text-foreground')
            }
          >
            {candidate.direction === 'credit' ? '+' : '−'}
            {formatCurrency(candidate.amount_cents)}
          </span>
          {exact ? (
            <Badge variant="success" className="text-[10px]">
              Exact match
            </Badge>
          ) : (
            <Badge variant="warning" className="text-[10px]">
              Δ {formatCurrency(candidate.amount_delta_cents)} vs{' '}
              {formatCurrency(sourceAmountCents)}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}
